// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState, useActionState } from 'react';

import {
  type CompanyId,
  type PartCatalog,
  type Part,
  type CreateEditPartBody,
  type PartAttributeDefinitionId,
  type PartAttributeDefinition,
  type PartListPart,
  PART_TYPE_ASSEMBLY_ID,
  type PartId,
  type PartCatalogId,
  useEvolveGet,
  useEvolvePost,
  useEvolvePut,
} from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useParams } from 'react-router';

import { uploadFileIfNeeded } from 'components/Mantine/FileUploads/uploadToPresignedUrl';
import { useEvolveLocation } from 'components/Mantine/Navigation/useEvolveLocation';
import type { StrongOmit, WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useManufacturers } from 'hooks/typeCacheHooks/useManufacturers';
import { useUser } from 'hooks/user/useUser';
import type { CreateEditPartFormBody, MoveToAssemblyEditorState } from 'modules/Shop/CatalogSetup/CreateEditPartFields';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

export type CreateEditPartFormType = WithNullable<
  CreateEditPartFormBody,
  Exclude<
    keyof CreateEditPartFormBody,
    'globalPartAttributes' | 'localPartAttributes' | 'assemblyPartItems' | 'assemblyWriteInItems' | 'assemblyTasks'
  >
>;

export type PartEditorContextType = {
  companyId: CompanyId;
  partCatalog: PartCatalog;
  selectedTab: 'details' | 'parts' | 'tasks';
  setSelectedTab: (tab: PartEditorContextType['selectedTab']) => void;
  part: Part | undefined;
  onPartUpdated: (part: Part) => void;
  form: UseFormReturnType<CreateEditPartFormType>;
  publishPart: (opts: {
    duplicateAsNew?: boolean;
    assembly?: Pick<
      CreateEditPartBody,
      'assemblyPartItems' | 'assemblyWriteInItems' | 'assemblyTasks' | 'assemblyEditorLayout'
    >;
    formValues: CreateEditPartFormType;
  }) => Promise<Part>;
  publishing: boolean;
  cachedAttributes: Record<PartAttributeDefinitionId, PartAttributeDefinition>;
  onAttributeLoaded: (attribute: PartAttributeDefinition) => void;
};

const PartEditorContext = createContext<PartEditorContextType | undefined>(undefined);

type ProviderProps = {
  part?: PartListPart;
  // If `part` is passed in but `partCategory` not passed in, will be looked up via a network call
  partCategory?: CategoryPath[number];
  // If `part` is passed in but `partCatalog` not passed in, will be looked up via a network call
  partCatalog?: PartCatalog;
  overrideDefaultValues?: Partial<CreateEditPartFormType>;
  children: ReactNode;
};

const PartEditorProviderWithForm = ({
  baseAssemblyEditorState,
  children,
  part,
  overrideDefaultValues,
  ...props
}: StrongOmit<PartEditorContextType, 'form' | 'publishing' | 'publishPart' | 'cachedAttributes' | 'onAttributeLoaded'> &
  Pick<ProviderProps, 'children' | 'overrideDefaultValues'> & {
    baseAssemblyEditorState: MoveToAssemblyEditorState | undefined;
    partCategory: CategoryPath[number] | undefined;
  }) => {
  const { manufacturers } = useManufacturers();
  const { apiCall: createPart, loading: creating } = useEvolvePost('shop/partCatalog/part');
  const { apiCall: updatePart, loading: updating } = useEvolvePut(`shop/partCatalog/part/:partId`);
  const { apiCall: duplicatePart, loading: duplicating } = useEvolvePost(`shop/partCatalog/part/:partId/duplicate`);
  const publishing = creating || updating || duplicating;

  const form = useForm<CreateEditPartFormType>({
    initialValues: baseAssemblyEditorState?.formValues ?? {
      partCategory: props.partCategory ?? null,
      unitOfMeasure: part?.unitOfMeasureName
        ? {
            unitOfMeasureId: part.unitOfMeasureId,
            name: part.unitOfMeasureName,
            code: part.unitOfMeasureCode,
          }
        : null,
      manufacturer: part?.manufacturerId
        ? {
            manufacturerId: part.manufacturerId,
            name: part.manufacturerName,
          }
        : (manufacturers?.find((m) => m.name === 'Generic') ?? null),
      manufacturerCatalogNumber: part?.manufacturerCatalogNumber ?? '',
      name: part?.name ?? '',
      description: part?.description ?? '',
      assemblyEditorLayout: part?.assemblyEditorLayout ?? null,
      globalPartAttributes:
        part?.globalPartAttributes.map((p) => ({
          partAttributeDefinitionId: p.partAttributeDefinitionId,
          partAttributeDefinitionVariantIds: p.variants.map((v) => v.partAttributeDefinitionVariantId),
        })) ?? [],
      localPartAttributes:
        part?.localPartAttributes.map((p) => ({
          name: p.name,
          allowWriteInVariant: p.allowWriteInVariant,
          variants: p.variants.map((v) => v.name),
        })) ?? [],
      assemblyPartItems: part?.assemblyPartItems ?? [],
      assemblyWriteInItems: part?.assemblyWriteInItems ?? [],
      assemblyTasks:
        part?.assemblyTasks.map((t) => ({
          assemblyTaskId: t.assemblyTaskId,
          taskTypeId: t.taskTypeId,
          predecessorAssemblyTaskIds: t.predecessors.map((p) => p.assemblyTaskId),
        })) ?? [],
      ...overrideDefaultValues,
    },
    initialDirty: baseAssemblyEditorState?.isDirty,
    validate: {
      partCategory: (v) => !v,
      unitOfMeasure: (v) => !v,
      manufacturer: (v) => !v,
      name: (v) => !v,
    },
    enhanceGetInputProps: () => ({ disabled: publishing }),
  });

  const [cachedAttributes, onAttributeLoaded] = useActionState<
    Record<PartAttributeDefinitionId, PartAttributeDefinition>,
    PartAttributeDefinition
  >(
    (cache, newAttribute) => ({
      ...cache,
      [newAttribute.partAttributeDefinitionId]: newAttribute,
    }),
    baseAssemblyEditorState?.cachedAttributes ??
      (part?.globalPartAttributes ?? []).reduce(
        (out, curr) => ({
          ...out,
          [curr.partAttributeDefinitionId]: curr,
        }),
        {},
      ),
  );

  const partExists = useMemo(() => isNotNil(part), [part]);

  const publishPart = useCallback<PartEditorContextType['publishPart']>(
    async ({ duplicateAsNew, assembly, formValues }) => {
      const { partCategory, manufacturer, unitOfMeasure, image, ...remainingValues } = formValues;
      const values = {
        partCategoryId: partCategory?.partCategoryId,
        manufacturerId: manufacturer?.manufacturerId,
        unitOfMeasureId: unitOfMeasure?.unitOfMeasureId,
        image: image
          ? {
              fileName: image.name,
            }
          : undefined,
        ...remainingValues,
        ...assembly,
        // Cast is validated as part of `form.validate`
      } as CreateEditPartBody;
      let updatedPart: Part;
      if (isNotNil(part)) {
        if (duplicateAsNew) {
          updatedPart = await duplicatePart(values, { url: `shop/partCatalog/part/${part.partId}/duplicate` });
        } else {
          updatedPart = await updatePart(values, { url: `shop/partCatalog/part/${part.partId}` });
        }
      } else {
        updatedPart = await createPart(values);
      }
      await uploadFileIfNeeded(image, updatedPart.image);

      notifications.show({
        title: `Successfully ${!partExists || duplicateAsNew ? 'created' : 'updated'}`,
        message: `${!partExists || duplicateAsNew ? 'Created' : 'Updated'} ${updatedPart.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'assembly' : 'part'} ${updatedPart.name}.`,
        color: 'green',
      });
      props.onPartUpdated(updatedPart);
      form.resetDirty(form.values);
      return updatedPart;
    },
    [createPart, duplicatePart, form, part, partExists, props, updatePart],
  );

  return (
    <PartEditorContext
      value={{
        ...props,
        part,
        form,
        publishing,
        publishPart,
        cachedAttributes,
        onAttributeLoaded,
      }}
    >
      {children}
    </PartEditorContext>
  );
};

export const PartEditorProvider = ({
  children,
  part: partFromParent,
  partCategory: parentPartCategory,
  partCatalog: partCatalogFromParent,
  overrideDefaultValues,
}: ProviderProps) => {
  const { user } = useUser();
  const { state } = useEvolveLocation();
  const [baseAssemblyEditorState] = useState(state?.additionalData as MoveToAssemblyEditorState | undefined);

  const [selectedTab, setSelectedTab] = useState<PartEditorContextType['selectedTab']>('details');

  const params = useParams();
  const initialPartId = params.partId as PartId | undefined;
  const initialPartCatalogId = params.partCatalogId as PartCatalogId | undefined;

  const [part, onPartUpdated] = useState<Part | undefined>(baseAssemblyEditorState?.part);
  const partId = partFromParent?.partId ?? part?.partId ?? initialPartId;
  const {
    data: partFromNetwork,
    loading: loadingPart,
    apiCall: getPartFromNetwork,
  } = useEvolveGet(`shop/partCatalog/part/:partId`);
  useEffect(() => {
    if (isNotNil(partId) && partId !== part?.partId) {
      void getPartFromNetwork({ url: `shop/partCatalog/part/${partId}` });
    }
  }, [getPartFromNetwork, part?.partId, partId]);
  useEffect(() => {
    if (isNotNil(partFromNetwork)) onPartUpdated(partFromNetwork);
  }, [partFromNetwork]);

  const {
    data: partCategoryFromNetwork,
    apiCall: getPartCategory,
    loading: loadingCategory,
  } = useEvolveGet(`shop/partCatalog/category/:partCategoryId`);
  const partCategory = useMemo(
    () =>
      parentPartCategory?.editable &&
      (isNil(partFromParent) || partFromParent.partCategoryId === parentPartCategory.partCategoryId)
        ? parentPartCategory
        : partCategoryFromNetwork,
    [parentPartCategory, partCategoryFromNetwork, partFromParent],
  );
  useEffect(() => {
    if (isNotNil(part?.partCategoryId) && partCategory?.partCategoryId !== part.partCategoryId) {
      void getPartCategory({ url: `shop/partCatalog/category/${part.partCategoryId}` });
    }
  }, [getPartCategory, partCategory?.partCategoryId, part?.partCategoryId]);

  const {
    data: partCatalogFromNetwork,
    apiCall: getPartCatalog,
    loading: loadingCatalog,
  } = useEvolveGet(`shop/partCatalog/:partCatalogId`);
  const partCatalog = useMemo(
    () =>
      baseAssemblyEditorState?.partCatalog ??
      (partCatalogFromParent?.editable ? partCatalogFromParent : partCatalogFromNetwork),
    [baseAssemblyEditorState?.partCatalog, partCatalogFromNetwork, partCatalogFromParent],
  );
  const partCatalogId = initialPartCatalogId ?? partCategory?.partCatalogId;
  useEffect(() => {
    if (isNotNil(partCatalogId) && partCatalogFromNetwork?.partCatalogId !== partCatalogId) {
      void getPartCatalog({ url: `shop/partCatalog/${partCatalogId}` });
    }
  }, [getPartCatalog, partCatalogFromNetwork?.partCatalogId, partCatalogId]);

  if (
    loadingCatalog ||
    loadingCategory ||
    loadingPart ||
    (isNotNil(part) && part.partCategoryId !== partCategory?.partCategoryId) ||
    isNil(partCatalog) ||
    (isNotNil(partFromNetwork) && partFromNetwork.partId !== part?.partId)
  ) {
    return <LoadingOverlay visible />;
  }

  return (
    <PartEditorProviderWithForm
      companyId={user.companyId}
      onPartUpdated={onPartUpdated}
      part={part}
      partCategory={partCategory}
      partCatalog={partCatalog}
      selectedTab={selectedTab}
      setSelectedTab={setSelectedTab}
      baseAssemblyEditorState={baseAssemblyEditorState}
      overrideDefaultValues={overrideDefaultValues}
    >
      {children}
    </PartEditorProviderWithForm>
  );
};

export const usePartEditor = () => useGeneralContext(PartEditorContext, 'PartEditor');
